'use client';

import { Portal } from '@common/components';
import { useBodyScrollLock } from '@common/hooks';
import { SvgDelete, SvgHamburger } from '@common/icon';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';

import { getMainWrapperElement } from '@/app/_components/MainWrapperPortal';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { ServiceShortcutPopoverContent } from './ServiceShortcutPopoverContent';

interface ServiceShortcutFullScreenModalProps {
  open: boolean;
  onClose: () => void;
}

export const ServiceShortcutFullScreenModal = ({
  open,
  onClose,
}: ServiceShortcutFullScreenModalProps) => {
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return (
    <Portal renderTo={getMainWrapperElement}>
      <AnimatePresence>
        {open ? (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="서비스 바로가기"
            className="fixed inset-0 z-50 flex flex-col bg-Gray-white"
            initial={{ opacity: 0, y: '4%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '4%' }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <header className="flex min-h-56 items-center px-20">
              <button
                type="button"
                onClick={onClose}
                className="flex size-24 items-center justify-center"
                aria-label="닫기"
              >
                <SvgDelete className="size-24 text-Gray-900" />
              </button>
              <h2 className="flex-1 text-center font-600 text-Gray-900 typo-body1">
                바로가기
              </h2>
              <div className="size-24" />
            </header>
            <div className="flex-1 overflow-auto">
              <ServiceShortcutPopoverContent
                onNavigate={onClose}
                contentClassName="pt-0 px-0"
                termsDrawerMode="bottom"
                closeOnTermsOpen={false}
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Portal>
  );
};

export const MobileServiceShortcutButton = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);

  useEffect(() => {
    if (isDesktop) setMenuOpen(false);
  }, [isDesktop]);

  return (
    <>
      <button
        type="button"
        aria-label="전체 메뉴"
        aria-expanded={menuOpen}
        onClick={openMenu}
        className="flex size-26 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900"
      >
        <SvgHamburger className="size-24" />
      </button>
      <ServiceShortcutFullScreenModal open={menuOpen} onClose={closeMenu} />
    </>
  );
};
